import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["editorToolbarStartSlot", "editorToolbarEndSlot", "Heading", "isBranchPreview", "tabsStartSlot", "tabsMiddleSlot", "tabsEndSlot", "tabsStyles", "titleText", "toolbarStyles", "data-test-id"];
import styled from 'styled-components';
import { EditorToolbar } from 'foundations-components/transitional/components/EditorToolbar';
import { TabBar } from 'foundations-components/transitional/components/TabBar';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { filterProps } from 'foundations-components/utils/filterProps';
import isSelenium from '../utils/isSelenium';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EPHeaderWrapper = styled.div.withConfig({
  displayName: "EPHeader__EPHeaderWrapper"
})(({
  $cssStyles,
  $isBranchPreview,
  theme: _theme
}) => {
  const theme = _theme;
  // Super gross to target the internals of `UITabBar` and `UIEditorToolbar`, especially using the names of components, but this was
  // recommended to us by Trellis folks: https://hubspot.slack.com/archives/C1WG3L3J6/p1740582560511349?thread_ts=1740518549.515709&cid=C1WG3L3J6

  return Object.assign({}, $cssStyles, {
    /**
     * Targetting the following structure:
     * <div class="EditorToolbar"> - Toolbar
     *  <div class="StyledFlex" role="group"> - Flex Wrapper
     *   <div class="StyledFlex"> - Another Flex wrapper
     *    <div class="StyledFlex"> - Slot
     *    <div class="StyledFlex"> - Slot
     *    <div class="StyledFlex"> - Slot
     *      <button> - Button that is immediate child of slot
     */
    '[class*="StyledFlex"][role="group"] > [class*="StyledFlex"]:first-of-type > [class*="StyledFlex"]': {
      flexBasis: '33.3%',
      paddingBlock: 0,
      paddingTop: 0,
      paddingBottom: 0,
      // Targets buttons as immediate children in flex slots in both TabBar and EditorToolbar
      // Similar to https://git.hubteam.com/HubSpot/UIComponents/blob/f8a0f921b6d718c143fd06142cbe9c8080444a0e/UIComponents/static/js/nav/UITabBar.tsx#L25-L29
      '> [class*="StyledButton"]': {
        flex: '0 0 auto'
      }
    },
    // Apply max inline size to the 'title' slot to prevent long titles from overflowing the header on small screens.
    '[class*="StyledFlex"][role="group"] [class*="StyledFlex"]:nth-of-type(2)': {
      minWidth: 0,
      flexGrow: 1
    },
    '[class*="EditorToolbar"] > [role="group"]': {
      paddingInlineStart: theme.space['500'],
      // Targets buttons as immediate children in flex slots in EditorToolbar, overriding styles above
      // Matches https://git.hubteam.com/HubSpot/UIComponents/blob/f8a0f921b6d718c143fd06142cbe9c8080444a0e/UIComponents/static/js/nav/AbstractToolbar.tsx#L64-L69
      ' > [class*="StyledFlex"]:first-of-type > [class*="StyledFlex"] > [class*="StyledButton"]': {
        flexShrink: 0,
        marginTop: 0,
        width: 'auto'
      }
    }
  }, $isBranchPreview && {
    '[data-test-id="ep-editor-toolbar"]': {
      background: 'linear-gradient(to bottom, rgba(59,103,158,1) 0%,rgba(43,136,217,1) 50%,rgba(32,124,202,1) 51%,rgba(125,185,232,1) 100%) !important',
      borderTop: `${theme.borderWidth['100']} solid`,
      borderImage: 'repeating-linear-gradient(10deg, #ff7a59, #ff7a59 3px, #fff1ee 3px, #fff1ee 6px) 1 !important'
    }
  });
});
const EPHeader = _ref => {
  let {
      editorToolbarStartSlot,
      editorToolbarEndSlot,
      Heading,
      isBranchPreview,
      tabsStartSlot,
      tabsMiddleSlot,
      tabsEndSlot,
      tabsStyles,
      titleText,
      toolbarStyles,
      'data-test-id': dataTestId = 'ep-editor-header'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const hasTabs = tabsStartSlot || tabsMiddleSlot || tabsEndSlot;
  const {
    tokenProps
  } = filterProps(rest);
  const wrapperStyles = useMapTokenPropsToCSS(tokenProps);

  // Branch preview styling is internal-only and interferes with Selenium tests,
  // so it is skipped when the `hs_selenium` cookie is present.
  const showBranchPreviewStyles = Boolean(isBranchPreview) && !isSelenium();
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(EPHeaderWrapper, {
      "data-test-id": dataTestId,
      $cssStyles: wrapperStyles,
      $isBranchPreview: showBranchPreviewStyles,
      children: [/*#__PURE__*/_jsx(EditorToolbar, Object.assign({
        startSlot: editorToolbarStartSlot,
        endSlot: editorToolbarEndSlot,
        Heading: Heading,
        titleText: titleText,
        "data-test-id": "ep-editor-toolbar"
      }, toolbarStyles)), hasTabs && /*#__PURE__*/_jsx(TabBar, Object.assign({
        startSlot: tabsStartSlot,
        middleSlot: tabsMiddleSlot,
        endSlot: tabsEndSlot,
        paddingBlockStart: "0",
        paddingInlineStart: "200",
        marginBlock: "200"
      }, tabsStyles))]
    })
  );
};
export const TrellisEPHeader = EPHeader;
export default EPHeader;